import type { PaAPI } from '@rialtic/types'
import { isEqual } from 'ohash'
import type { AnswerValue } from './contract'
import type { CreatePolicyInput } from './messages'
import { asList } from './contract'

/**
 * Contract rows Content Library stores too. A room's answers are the draft;
 * PaAPI's copy is the baseline publish writes the changed rows into.
 */
export const PAAPI_ROWS = [
  'policy-name',
  'topic',
  'logic-types',
  'claim-types',
  'description',
  'policy-effective-start-date',
  'policy-effective-end-date',
] as const
export type PaapiRow = (typeof PAAPI_ROWS)[number]

/** Content Library's value for each row it has one for. */
export type PaapiAnswers = Partial<Record<PaapiRow, AnswerValue>>

/** A row publish would write: the draft differs from Content Library. */
export interface PaapiChange {
  fieldId: PaapiRow
  /** Content Library's value; null when it has none. */
  from: AnswerValue | null
  to: AnswerValue
}

/** Content Library's copy of a policy, read on demand. */
export interface PaapiBaseline {
  /** Null when PaAPI could not be read. */
  answers: PaapiAnswers | null
  error: string | null
}

/** Every claim type PaAPI flags a policy with, by its contract label. */
export const CLAIM_TYPE_FLAGS = {
  Professional: 'is_claim_type_professional',
  'ASC Billed on Professional': 'is_claim_type_asc_professional',
  'ASC Billed on Institutional': 'is_claim_type_asc_institutional',
  'Institutional Outpatient': 'is_claim_type_outpatient',
  'Institutional Inpatient': 'is_claim_type_inpatient',
} as const

const LIST_ROWS: readonly PaapiRow[] = ['logic-types', 'claim-types']
const DATE_ROWS: readonly PaapiRow[] = [
  'policy-effective-start-date',
  'policy-effective-end-date',
]

/** One row as Content Library would store it; undefined when it has no value. */
const normalize = (
  row: PaapiRow,
  value: AnswerValue | null | undefined,
): AnswerValue | undefined => {
  if (LIST_ROWS.includes(row)) {
    if (!Array.isArray(value)) return undefined
    /* No claim type is still an answer: PaAPI stores it as every flag off. */
    return value.length || row === 'claim-types' ? value.toSorted() : undefined
  }
  const text = typeof value === 'string' ? value.trim() : ''
  if (DATE_ROWS.includes(row))
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : undefined
  return text || undefined
}

/** The Content Library rows out of a set of answers, normalized for comparison. */
export const paapiAnswers = (
  answers: Partial<Record<string, AnswerValue | null>>,
): PaapiAnswers =>
  Object.fromEntries(
    PAAPI_ROWS.flatMap((row) => {
      const value = normalize(row, answers[row])
      return value === undefined ? [] : [[row, value]]
    }),
  )

/**
 * Rows publish would write. An unanswered draft row is not a change: publish
 * leaves Content Library's value alone rather than clearing it.
 */
export const paapiChanges = (
  draft: Record<string, AnswerValue>,
  baseline: PaapiAnswers,
): PaapiChange[] =>
  Object.entries(paapiAnswers(draft)).flatMap(([row, to]) => {
    const from = baseline[row as PaapiRow] ?? null
    return isEqual(to, from)
      ? []
      : [{ fieldId: row as PaapiRow, from, to: to as AnswerValue }]
  })

/** PaAPI's topic and edit-type lists, for mapping its ids to contract values and back. */
export interface PaapiVocabulary {
  topics: { id: string; topic: string }[]
  editTypes: { id: string; edit_type: string }[]
}

/** The slice of PaAPI's `GET /policy?result=min` row this reads. */
export interface PaapiPolicyRecord {
  name: string
  description?: string | null
  topic_id?: string | null
  edit_types?: { edit_type_id: string }[]
  is_claim_type_professional?: boolean
  is_claim_type_asc_professional?: boolean
  is_claim_type_asc_institutional?: boolean
  is_claim_type_outpatient?: boolean
  is_claim_type_inpatient?: boolean
  effective_date_start?: string | null
  effective_date_end?: string | null
}

/** The description a triage-accepted policy is created with, until scoping
 *  writes the client-facing one. Never read back as a proposal. */
export const DRAFT_DESCRIPTION = 'Being scoped in Policy Studio.'

/** Content Library's policy, as the contract rows it owns. */
export const answersFromPaapi = (
  policy: PaapiPolicyRecord,
  { topics, editTypes }: PaapiVocabulary,
): PaapiAnswers =>
  paapiAnswers({
    'policy-name': policy.name,
    topic: topics.find(({ id }) => id === policy.topic_id)?.topic,
    description:
      policy.description === DRAFT_DESCRIPTION ? null : policy.description,
    'logic-types': (policy.edit_types ?? []).flatMap(
      ({ edit_type_id }) =>
        editTypes.find(({ id }) => id === edit_type_id)?.edit_type ?? [],
    ),
    'claim-types': Object.entries(CLAIM_TYPE_FLAGS).flatMap(([label, flag]) =>
      policy[flag] ? [label] : [],
    ),
    'policy-effective-start-date': policy.effective_date_start,
    'policy-effective-end-date': policy.effective_date_end,
  })

const idOf = <T extends { id: string }>(
  list: T[],
  matches: (entry: T) => boolean,
  what: string,
): string => {
  const found = list.find(matches)
  if (!found) throw new Error(`Content Library has no ${what}`)
  return found.id
}

/**
 * The `PUT /policy/{id}` body for `changes`, carrying only the columns they
 * touch.
 *
 * @throws When a topic or logic type has no PaAPI id.
 */
export const paapiUpdateBody = (
  changes: PaapiChange[],
  { topics, editTypes }: PaapiVocabulary,
): PaAPI.PutBody['/policy/{id}'] => {
  const body: PaAPI.PutBody['/policy/{id}'] = {
    management_source: 'policy_studio',
  }
  for (const { fieldId, to } of changes) {
    const text = String(to)
    switch (fieldId) {
      case 'policy-name':
        body.name = text
        break
      case 'description':
        body.description = text
        break
      case 'topic':
        body.topic = idOf(
          topics,
          ({ topic }) => topic === text,
          `topic "${text}"`,
        )
        break
      case 'logic-types':
        body.edit_types = asList(to).map((name) => ({
          edit_type_id: idOf(
            editTypes,
            ({ edit_type }) => edit_type === name,
            `logic type "${name}"`,
          ),
        }))
        break
      case 'claim-types':
        for (const [label, flag] of Object.entries(CLAIM_TYPE_FLAGS))
          body[flag] = asList(to).includes(label)
        break
      case 'policy-effective-start-date':
        body.effective_date_start = text
        break
      case 'policy-effective-end-date':
        body.effective_date_end = text
        break
    }
  }
  return body
}

/**
 * The `POST /policy` body that creates a triage-accepted policy in Content
 * Library from its name, topic and logic types.
 *
 * @throws When there is no topic, or a value has no PaAPI id.
 */
export const paapiCreateBody = (
  input: Pick<CreatePolicyInput, 'topic' | 'logicTypes'> & { title: string },
  vocabulary: PaapiVocabulary,
): PaAPI.PostBody['/policy'] => {
  const changes = paapiChanges(
    { topic: input.topic ?? '', 'logic-types': input.logicTypes ?? [] },
    {},
  )
  const body = paapiUpdateBody(changes, vocabulary)
  if (!body.topic)
    throw new Error('Content Library needs a topic to create the policy')
  return {
    ...body,
    name: input.title,
    topic: body.topic,
    description: DRAFT_DESCRIPTION,
  }
}
